import {
	type ContractSuretyAccountUnderwritingModelSize,
	financialStatementAttachmentTypeLabelMap,
	humanizeFileValidationMessage,
} from "@inrev/common";
import { FormSection } from "@portal/components/form";
import { FormFieldError } from "@portal/components/form/FormFieldError";
import {
	FormCurrencyLineItem,
	FormDateLineItem,
	FormYesNoLineItem,
} from "@portal/components/form/fieldLayouts";
import { Card, PartialIndicator } from "@portal/components/primitives";
import { FileUpload } from "@portal/components/ui/Upload";
import { trackingAttrs } from "@portal/components/utils";
import { DateTime } from "luxon";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { type AccountFormValues } from "../../schema";

type SectionProps = {
	formMethods: ReturnType<typeof useFormContext<AccountFormValues>>;
	isLarge: boolean;
};

export const FinancialsStep = () => {
	const formMethods = useFormContext<AccountFormValues>();
	const { control } = formMethods;
	const modelSize = useWatch({
		control,
		name: "account.modelSize",
	}) as ContractSuretyAccountUnderwritingModelSize;
	const isMediumOrLarge = modelSize === "medium" || modelSize === "large";
	const isLarge = modelSize === "large";

	const effectiveFyeYear = DateTime.now().year - 1;

	return (
		<div className="flex flex-col pt-[18px] pb-[125px] min-h-fit space-y-[40px]">
			<AiFinancialDocumentsSection formMethods={formMethods} modelSize={modelSize} />

			<div className="w-full h-fit flex flex-col space-y-[35px]">
				<ProfitabilitySection
					formMethods={formMethods}
					showLastFye={!isMediumOrLarge}
					effectiveFyeYear={effectiveFyeYear}
				/>
				{isMediumOrLarge && <WorkInProgressSection formMethods={formMethods} isLarge={isLarge} />}
				{isLarge && <BankDetailsSection formMethods={formMethods} />}
			</div>
		</div>
	);
};

const ProfitabilitySection = ({
	formMethods,
	showLastFye = true,
	effectiveFyeYear,
}: {
	formMethods: ReturnType<typeof useFormContext<AccountFormValues>>;
	showLastFye?: boolean;
	effectiveFyeYear: number;
}) => (
	<Card elevation="raised" className="w-full h-fit" id="profitability">
		<Card.Header>
			<Card.Title className="text-[15.5px] font-[550]">Profitability</Card.Title>
		</Card.Header>
		<Card.Body surface="gray" className="flex flex-col py-[35px] px-[45px] space-y-[18px]">
			<FormYesNoLineItem
				formMethods={formMethods}
				name="account.financials.profitability.profitableYtd"
				label={`Profitable YTD${effectiveFyeYear ? ` (${effectiveFyeYear + 1})` : ""}`}
				deselectable
			/>
			{showLastFye && (
				<FormYesNoLineItem
					formMethods={formMethods}
					name="account.financials.profitability.profitableLastFye"
					label={`Profitable Last Year${effectiveFyeYear ? ` (FY${effectiveFyeYear})` : ""}`}
					deselectable
				/>
			)}
		</Card.Body>
	</Card>
);

const WorkInProgressSection = ({ formMethods, isLarge }: SectionProps) => (
	<Card elevation="raised" className="w-full h-fit" id="wip">
		<Card.Header>
			<Card.Title className="text-[15.5px] font-[550]">Work In Progress</Card.Title>
			<Card.Actions>
				<div
					id="pendo-wip-statement"
					{...trackingAttrs("account-submission-wip-help")}
					className="text-[12px] text-link hover:text-link-hover font-medium underline cursor-pointer"
				>
					Where do I find this?
				</div>
			</Card.Actions>
		</Card.Header>
		<Card.Body surface="gray" className="flex flex-col py-[35px] px-[45px] space-y-[18px]">
			<FormDateLineItem
				formMethods={formMethods}
				name="account.financials.workInProgressStatement.date"
				label="Statement Date"
				disableAfter={DateTime.now().endOf("day").toISODate()}
			/>
			<FormCurrencyLineItem
				formMethods={formMethods}
				name="account.financials.workInProgressStatement.projectBacklogCost"
				label="Backlog Cost-to-Complete"
			/>
			{isLarge && (
				<FormCurrencyLineItem
					formMethods={formMethods}
					name="account.financials.workInProgressStatement.projectBacklogGrossProfit"
					label="Gross Profit in the Backlog"
					tooltip={
						"<table width='500'><tr><td>This is the total gross profit to be earned on all uncompleted work (total estimated gross profit for all projects - gross profit earned to date for all projects). It can be found on the Work in Progress statement and is usually listed as \"Gross Profit Remaining\"</td></tr></table>"
					}
				/>
			)}
		</Card.Body>
	</Card>
);

const BankDetailsSection = ({
	formMethods,
}: {
	formMethods: ReturnType<typeof useFormContext<AccountFormValues>>;
}) => (
	<Card elevation="raised" className="w-full h-fit" id="bankDetails">
		<Card.Header>
			<Card.Title className="text-[15.5px] font-[550]">Bank Details</Card.Title>
		</Card.Header>
		<Card.Body surface="gray" className="flex flex-col py-[35px] px-[45px] space-y-[18px]">
			<FormCurrencyLineItem
				formMethods={formMethods}
				name="account.financials.blocSize"
				label="Revolving Line of Credit Limit"
			/>
		</Card.Body>
	</Card>
);

const aiDropzoneHeaders: Record<ContractSuretyAccountUnderwritingModelSize, React.ReactNode> = {
	small: (
		<>
			Uploads are <strong>optional</strong> for this submission &mdash; the profitability
			information below is sufficient on its own. Include any interim and FYE financial statements
			if you have them.
		</>
	),
	medium: (
		<>
			Please provide <strong>the last FYE financial statement</strong> below. Include the latest
			interim and other FYE statements if you have them.
		</>
	),
	large: (
		<>
			Please provide <strong>the last two FYE financial statements</strong> below. Include the
			latest interim and other FYE statements if you have them.
		</>
	),
};

const AiFinancialDocumentsSection = ({
	formMethods,
	modelSize,
}: {
	formMethods: ReturnType<typeof useFormContext<AccountFormValues>>;
	modelSize: ContractSuretyAccountUnderwritingModelSize;
}) => {
	const {
		control,
		formState: { isSubmitted },
	} = formMethods;
	const { field, fieldState } = useController({
		name: "account.financials.files",
		control,
	});
	const isSmall = modelSize === "small";
	const showPartial = !isSmall && isSubmitted && field.value.length === 0;
	const filesErrorMessage = getFilesFieldErrorMessage(fieldState.error);

	return (
		<FormSection
			topPadding
			headerClassName="[&>span]:flex-1"
			header={
				<div className="w-full flex items-center">
					<div className="flex-1 flex items-center gap-2">
						Financial Documents
						{showPartial && <PartialIndicator active />}
					</div>
					<div
						id="pendo-ai-disclosure"
						{...trackingAttrs("account-submission-ai-disclosure")}
						className="text-[12px] text-link hover:text-link-hover font-medium underline cursor-pointer"
					>
						How this works
					</div>
				</div>
			}
			subHeader={aiDropzoneHeaders[modelSize] ?? aiDropzoneHeaders.small}
			id="financialDocuments"
		>
			<div className="relative">
				<FileUpload
					value={field.value}
					onChange={field.onChange}
					onBlur={field.onBlur}
					allowedTypesAndLabels={financialStatementAttachmentTypeLabelMap}
					enableTypeSelection={false}
					expandedByDefault
					allowDelete
					hideChecklist
					analyticsContext="submission-upload"
					trackingPrefix="account-submission-financial-documents"
				/>
				{filesErrorMessage && (
					<FormFieldError
						error={{ type: "error", message: filesErrorMessage }}
						className="top-auto bottom-[-8px]"
					/>
				)}
			</div>
		</FormSection>
	);
};

const getFilesFieldErrorMessage = (error: unknown): string | undefined => {
	if (!error || typeof error !== "object") return undefined;
	const record = error as Record<string, unknown>;
	if ("message" in record && typeof record.message === "string" && record.message) {
		return humanizeFileValidationMessage(record.message);
	}
	if (Array.isArray(error)) {
		for (const item of error) {
			const message = getFilesFieldErrorMessage(item);
			if (message) return message;
		}
	}
	for (const child of Object.values(record)) {
		const message = getFilesFieldErrorMessage(child);
		if (message) return message;
	}
	return "One or more uploaded files couldn't be validated. Remove and re-upload the affected files.";
};
